
import Vue from 'vue';
import { Moment } from 'moment';
import { Component, Prop } from 'vue-property-decorator';
import linkifyStr from 'linkify-string';
import { AppointmentsQueue } from '@/types/AppointmentsQueue';
import AppointmentsDisplay from '@/components/appointments/AppointmentsDisplay.vue';
import AppointmentsSchedule from '@/components/appointments/AppointmentsSchedule.vue';
import { Appointment, AppointmentSlot } from '@/types/Appointment';
import ErrorDialog from '@/util/ErrorDialog';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
	faSignature,
	faAt,
	faQuestion,
	faLink,
	faChalkboardTeacher,
	faHandPaper,
	faCalendarTimes,
	faCog,
	faMapMarker,
} from '@fortawesome/free-solid-svg-icons';

library.add(
	faSignature,
	faAt,
	faQuestion,
	faLink,
	faChalkboardTeacher,
	faHandPaper,
	faCalendarTimes,
	faCog,
	faMapMarker
);

@Component({
	components: { AppointmentsDisplay },
})
export default class AppointmentsAdminSelector extends Vue {
	name = '';
	description = '';
	location = '';

	@Prop({ required: true }) queue!: AppointmentsQueue;
	@Prop({ required: true }) time!: Moment;
	@Prop({ required: true }) loaded!: boolean;

	get appointments() {
		if (this.queue.schedule === undefined) {
			return undefined;
		}

		return this.queue.schedule.appointmentSlots;
	}

	appointmentSelected(timeslot: number | null, index: number | null) {
		this.selectedTimeslot = timeslot;
		this.selectedIndex = index;
	}

	selectedTimeslot: number | null = null;
	selectedIndex: number | null = null;

	get selectedAppointment(): AppointmentSlot | null {
		if (
			this.selectedTimeslot === null ||
			this.selectedIndex === null ||
			this.queue.schedule === undefined
		) {
			return null;
		}

		return this.queue.schedule.timeslots[this.selectedTimeslot]!.slots[
			this.selectedIndex
		]!;
	}

	get selectedAppointmentInPast(): boolean {
		return (
			this.selectedAppointment !== null &&
			this.selectedAppointment.scheduledTime
				.clone()
				.add(this.selectedAppointment.duration, 'minutes') < this.time
		);
	}

	get selectedAppointmentLocation(): string {
		if (
			this.selectedAppointment === null ||
			!this.selectedAppointment.filled ||
			(this.selectedAppointment as Appointment).location === undefined
		) {
			return '';
		}

		return linkifyStr((this.selectedAppointment as Appointment).location!, {
			defaultProtocol: 'https',
		});
	}

	claimTimeslot() {
		fetch(
			process.env.BASE_URL +
				`api/queues/${this.queue.id}/appointments/${this.queue.schedule?.day}/claims/${this.selectedTimeslot}`,
			{
				method: 'PUT',
			}
		).then((res) => {
			if (res.status !== 201) {
				return ErrorDialog(res);
			}
		});
	}

	unclaimAppointment() {
		fetch(
			process.env.BASE_URL +
				`api/queues/${this.queue.id}/appointments/claims/${
					(this.selectedAppointment as Appointment).id
				}`,
			{
				method: 'DELETE',
			}
		).then((res) => {
			if (res.status !== 201) {
				return ErrorDialog(res);
			}
		});
	}

	editSchedule() {
		fetch(
			process.env.BASE_URL + `api/queues/${this.queue.id}/appointments/schedule`
		)
			.then((res) => res.json())
			.then((schedule: [{ [index: string]: any }]) => {
				schedule.sort(
					(a: { [index: string]: any }, b: { [index: string]: any }) =>
						a['day'] - b['day']
				);
				const schedules = schedule.map((s: { [index: string]: any }) =>
					s['schedule'].split('').map((i: string) => parseInt(i))
				);
				const durations = schedule.map(
					(s: { [index: string]: any }) => s['duration']
				);
				this.$buefy.modal.open({
					parent: this,
					component: AppointmentsSchedule,
					props: { defaultSchedules: schedules, defaultDurations: durations },
					events: {
						saved: (day: number, duration: number, schedule: number[]) => {
							const scheduleStr = schedule
								.map((slot: number) => slot.toString())
								.join('');
							fetch(
								process.env.BASE_URL +
									`api/queues/${this.queue.id}/appointments/schedule/${day}`,
								{
									method: 'PUT',
									body: JSON.stringify({
										duration: duration,
										padding: 2,
										schedule: scheduleStr,
									}),
								}
							).then((res) => {
								if (res.status !== 204) {
									return ErrorDialog(res);
								}
							});
						},
					},
					hasModalCard: true,
					trapFocus: true,
				});
			});
	}
}
